import React, { useEffect, useRef, useState } from "react";
import {
  AREA_FILTERS,
  PRODUCT_TYPE_FILTERS,
  getAvailableFilterCounts,
} from "../../utils/productFilters";

const getSelectedSummary = (filters, selected) => {
  if (selected.length === 0) return null;

  const labels = filters
    .filter(({ id }) => selected.includes(id))
    .map(({ label }) => label);

  if (labels.length <= 2) return labels.join(", ");
  return `${labels.length} selected`;
};

const FilterDropdown = ({ title, filters, selected, onToggle, counts }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const visibleFilters = filters.filter(
    ({ id }) => (counts[id] ?? 0) > 0 || selected.includes(id),
  );
  const summary = getSelectedSummary(filters, selected);
  const selectedCount = selected.length;

  return (
    <div ref={containerRef} className="relative">
      <label className="mb-1 block text-[9px] font-semibold uppercase tracking-[0.18em] text-amber-300/80 sm:text-[10px]">
        {title}
      </label>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex h-9 w-full items-center justify-between gap-2 rounded-lg border border-white/10 bg-slate-900/80 px-3 text-left text-xs text-gray-200 transition-colors hover:border-amber-400/30 focus:border-amber-400/50 focus:outline-none focus:ring-1 focus:ring-amber-400/30 sm:h-10 sm:text-sm"
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className={`min-w-0 truncate ${summary ? "text-white" : "text-gray-400"}`}>
          {summary || `All ${title.toLowerCase()}`}
        </span>
        <span className="flex shrink-0 items-center gap-1.5">
          {selectedCount > 0 && (
            <span className="flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-amber-400 px-1.5 text-[10px] font-semibold text-black">
              {selectedCount}
            </span>
          )}
          <svg
            className={`h-4 w-4 text-amber-300/80 transition-transform ${open ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>

      {open && (
        <div
          className="absolute left-0 right-0 z-30 mt-1 max-h-52 overflow-y-auto rounded-xl border border-white/10 bg-slate-900 py-1 shadow-xl shadow-black/40 [-webkit-overflow-scrolling:touch]"
          role="listbox"
          aria-label={title}
          aria-multiselectable="true"
        >
          {visibleFilters.map(({ id, label }) => {
            const count = counts[id] ?? 0;
            const isSelected = selected.includes(id);

            return (
              <button
                key={id}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => onToggle(id)}
                className={`flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm transition-colors ${
                  isSelected
                    ? "bg-amber-400/10 text-white"
                    : "text-gray-200 hover:bg-white/5"
                }`}
              >
                <span
                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                    isSelected
                      ? "border-amber-400 bg-amber-400 text-black"
                      : "border-white/20 bg-transparent"
                  }`}
                  aria-hidden="true"
                >
                  {isSelected && (
                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </span>
                <span className="min-w-0 flex-1 truncate">{label}</span>
                <span className="shrink-0 text-xs text-gray-400">({count})</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ProductFilters = ({
  productTypes,
  areas,
  onToggleProductType,
  onToggleArea,
  onClearAll,
  productList,
  resultCount,
  totalCount,
}) => {
  const { typeCounts, areaCounts } = getAvailableFilterCounts(productList, {
    productTypes,
    areas,
  });
  const hasActiveFilters = productTypes.length > 0 || areas.length > 0;

  return (
    <div className="mb-3 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2.5 sm:px-4 sm:py-3">
      <div className="mb-2 flex items-center justify-between gap-3">
        <p className="text-[9px] font-medium uppercase tracking-[0.16em] text-gray-400 sm:text-[10px]">
          Showing{" "}
          <span className="text-amber-300/90">{resultCount}</span> of{" "}
          <span className="text-amber-300/90">{totalCount}</span> products
        </p>
        {hasActiveFilters && (
          <button
            type="button"
            onClick={onClearAll}
            className="shrink-0 text-[9px] font-semibold uppercase tracking-[0.12em] text-amber-300/80 transition-colors hover:text-amber-200 sm:text-[10px]"
          >
            Clear all filters
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 sm:gap-3">
        <FilterDropdown
          title="Product type"
          filters={PRODUCT_TYPE_FILTERS}
          selected={productTypes}
          onToggle={onToggleProductType}
          counts={typeCounts}
        />
        <FilterDropdown
          title="Area"
          filters={AREA_FILTERS}
          selected={areas}
          onToggle={onToggleArea}
          counts={areaCounts}
        />
      </div>

    </div>
  );
};

export default ProductFilters;
